import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react'
import type { LoginResponse } from '../api/auth'
import { SortKeyTh, SortResetLink } from '../components/SortKeyTh'
import {
  bankApi,
  INCOME_CATEGORIES,
  MATCH_LABELS,
  STATUS_LABELS,
  type Anomalies,
  type AllocationItem,
  type AllocationTargetType,
  type BankMovement,
  type CsvProfile,
  type Direction,
  type ImportBatch,
  type ImportPreview,
  type MonthStatus,
  type MovementAllocation,
  type MovementRule,
  type ReconciliationStatus,
  type SuggestionsResponse,
  type TransferCandidate,
} from '../api/bank'
import { Dialog, FormGrid, Button, Callout, Card, CardContent, CardHeader, Checkbox, CircularProgress, DataTable, DataTableEmptyRow, EmptyState, FilterBar, IconButton, List, ListItem, Page, PageHeader, PaneLayout, Pagination, Select, StatusPill, Tabs, TextField, useConfirm, type PillTone, usePrompt } from '../components/ui'
import { expenseCategoriesApi, financialAccountsApi, suppliersApi } from '../api/finance-ext'
import { useFinanceDimensions } from '../lib/financeDimensions'
import { incomeCategoriesApi, type IncomeCategory } from '../api/costs'

/**
 * F-077 (TASK-0331) — movimenti bancari, import dell'estratto conto (CSV), regole, giroconti, split e riconciliazione.
 *
 * <b>Cosa NON c'è</b>: integrazione bancaria o open banking (BLK-004), Excel, IBAN in qualunque forma, abbinamenti per
 * pratica/operatore/crediti (DEC-004), tesoreria e previsioni. Il Commercialista vede tutto in sola lettura. Il gating
 * reale è lato server (FinanceAdministration per le scritture, FinanceAdministrationRead per le letture).
 */

type Tab = 'movements' | 'import' | 'rules' | 'reconcile' | 'anomalies'

interface Lookups {
  accounts: { id: number; name: string }[]
  categories: { id: number; name: string }[]
  suppliers: { id: number; companyName: string }[]
}

function errMsg(e: unknown, fallback: string): string {
  return e instanceof Error ? e.message : fallback
}

function eur(n: number): string {
  return n.toLocaleString('it-IT', { style: 'currency', currency: 'EUR' })
}

function fmtDate(s: string | null | undefined): string {
  if (!s) return '—'
  const [y, m, d] = s.slice(0, 10).split('-')
  return `${d}/${m}/${y}`
}

function isAdminRole(auth: LoginResponse): boolean {
  return auth.role === 'Administrator' || auth.role === 'EqualAdmin'
}

function canWriteRole(auth: LoginResponse): boolean {
  return isAdminRole(auth) || auth.role === 'EqualAmministrazione'
}

function statusBadge(status: ReconciliationStatus): PillTone {
  switch (status) {
    case 'Riconciliato': return 'success'
    case 'Parziale': return 'warning'
    case 'NonRiconciliabile': return 'neutral'
    default: return 'error'
  }
}

export function BankPage({ auth }: { auth: LoginResponse }) {
  const [tab, setTab] = useState<Tab>('movements')
  const [lookups, setLookups] = useState<Lookups>({ accounts: [], categories: [], suppliers: [] })
  const canWrite = canWriteRole(auth)

  useEffect(() => {
    void (async () => {
      const [accounts, categories, suppliers] = await Promise.all([
        financialAccountsApi.list().catch(() => []),
        expenseCategoriesApi.list().catch(() => []),
        suppliersApi.list().catch(() => []),
      ])
      setLookups({ accounts, categories, suppliers })
    })()
  }, [])

  const tabs: { id: Tab; label: string }[] = [
    { id: 'movements', label: 'Movimenti' },
    { id: 'import', label: 'Importa estratto conto' },
    { id: 'rules', label: 'Regole' },
    { id: 'reconcile', label: 'Riconciliazione' },
    { id: 'anomalies', label: 'Anomalie' },
  ]

  return (
    <Page>
      <PageHeader
        title="Banca"
        icon="account_balance_wallet"
        actions={!canWrite ? <StatusPill tone="neutral">Sola lettura</StatusPill> : undefined}
      />

      <Tabs aria-label="Sezioni banca" items={tabs} value={tab} onChange={id => setTab(id as Tab)} />

      {lookups.accounts.length === 0 && (
        <Callout tone="info">
          Nessun conto attivo: crea prima un conto in «Finanza» per registrare o importare i movimenti.
        </Callout>
      )}

      {tab === 'movements' && <MovementsTab lookups={lookups} canWrite={canWrite} />}
      {tab === 'import' && <ImportTab lookups={lookups} canWrite={canWrite} />}
      {tab === 'rules' && <RulesTab lookups={lookups} canWrite={canWrite} />}
      {tab === 'reconcile' && <ReconcileTab canWrite={canWrite} isAdmin={isAdminRole(auth)} />}
      {tab === 'anomalies' && <AnomaliesTab />}
    </Page>
  )
}

/* ── Dimensioni del movimento (R4-03, flag finanza.dimensioni_estese) ────────────────────────── */

function MovementDimensionsDialog({ movement, dims, onClose, onSaved }: {
  movement: BankMovement
  dims: ReturnType<typeof useFinanceDimensions>
  onClose: () => void
  onSaved: () => Promise<void>
}) {
  const [bu, setBu] = useState(movement.businessUnitId ? String(movement.businessUnitId) : '')
  const [site, setSite] = useState(movement.siteId ? String(movement.siteId) : '')
  const [team, setTeam] = useState(movement.teamId ? String(movement.teamId) : '')
  const [income, setIncome] = useState(movement.incomeCategoryId ? String(movement.incomeCategoryId) : '')
  const [categories, setCategories] = useState<IncomeCategory[]>([])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const isIncome = movement.direction === 'Entrata'

  useEffect(() => {
    if (isIncome) void incomeCategoriesApi.list().then(setCategories).catch(() => setCategories([]))
  }, [isIncome])

  // Il server legge 0 come «togli», assente come «lascia com'era».
  const idOrClear = (v: string): number => (v ? Number(v) : 0)

  async function save(e: FormEvent) {
    e.preventDefault()
    setSaving(true)
    setError('')
    try {
      await bankApi.updateMovement(movement.id, {
        businessUnitId: idOrClear(bu),
        siteId: idOrClear(site),
        teamId: idOrClear(team),
        ...(isIncome ? { incomeCategoryId: idOrClear(income) } : {}),
      })
      await onSaved()
    } catch (err) {
      setError(errMsg(err, 'Impossibile salvare le dimensioni'))
      setSaving(false)
    }
  }

  const pick = (label: string, options: { id: number; label: string }[], value: string, set: (v: string) => void) => (
    <Select label={label} value={value} onChange={ev => set(ev.target.value)}>
      <option value="">Non assegnato</option>
      {value && !options.some(o => String(o.id) === value) && <option value={value}>#{value}</option>}
      {options.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
    </Select>
  )

  return (
    <Dialog
      open
      onClose={onClose}
      title="Dimensioni del movimento"
      supportingText="Business unit, sede, team e categoria di ricavo servono ai report: non cambiano lo stato di riconciliazione."
      as="form"
      onSubmit={save}
      fullscreenOnCompact
      actions={(
        <>
          <Button variant="text" type="button" dismiss onClick={onClose}>Annulla</Button>
          <Button variant="text" type="submit" disabled={saving}>{saving ? 'Salvataggio…' : 'Salva'}</Button>
        </>
      )}
    >
      {error && <Callout tone="error">{error}</Callout>}
      <FormGrid>
        {dims.businessUnits.length > 0 && pick('Business unit', dims.businessUnits, bu, setBu)}
        {dims.sites.length > 0 && pick('Sede', dims.sites, site, setSite)}
        {pick('Team', dims.teams, team, setTeam)}
        {isIncome && pick('Categoria di ricavo', categories.filter(c => c.isActive).map(c => ({ id: c.id, label: c.name })), income, setIncome)}
      </FormGrid>
    </Dialog>
  )
}

/* ── Movimenti ───────────────────────────────────────────────────────────────────────────────── */

/** F-101: ordinamento di default di GET /api/finance/movements (data operazione decrescente). */
const MOVEMENTS_DEFAULT_SORT = 'date_desc'

function MovementsTab({ lookups, canWrite }: { lookups: Lookups; canWrite: boolean }) {
  const [sort, setSort] = useState(MOVEMENTS_DEFAULT_SORT)
  const prompt = usePrompt()
  const [accountId, setAccountId] = useState('')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [status, setStatus] = useState('')
  const [direction, setDirection] = useState('')
  const [includeVoided, setIncludeVoided] = useState(false)
  const [page, setPage] = useState(1)
  const [data, setData] = useState<Awaited<ReturnType<typeof bankApi.movements>> | null>(null)
  const [error, setError] = useState('')
  const [info, setInfo] = useState('')
  const [loading, setLoading] = useState(false)
  const [showForm, setShowForm] = useState(false)
  const dims = useFinanceDimensions()
  const [dimTarget, setDimTarget] = useState<BankMovement | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      setData(await bankApi.movements({
        accountId: accountId ? Number(accountId) : undefined,
        from: from || undefined,
        to: to || undefined,
        status: status || undefined,
        direction: direction || undefined,
        includeVoided,
        sort,
        page,
        pageSize: 50,
      }))
    } catch (e) {
      setError(errMsg(e, 'Errore durante il caricamento dei movimenti'))
    } finally {
      setLoading(false)
    }
  }, [accountId, from, to, status, direction, includeVoided, sort, page])

  function handleSort(next: string) { setSort(next); setPage(1) }

  useEffect(() => { void load() }, [load])

  async function classify(m: BankMovement, value: string) {
    setError('')
    try {
      await bankApi.updateMovement(m.id, m.direction === 'Uscita'
        ? { updateClassification: true, expenseCategoryId: value ? Number(value) : null, supplierId: m.supplierId }
        : { updateClassification: true, incomeCategory: value || null, supplierId: m.supplierId })
      await load()
    } catch (e) {
      setError(errMsg(e, 'Impossibile salvare la categoria'))
    }
  }

  async function saveRule(m: BankMovement) {
    setError('')
    setInfo('')
    const pattern = await prompt({
      title: 'Crea una regola dal movimento',
      message: 'Testo da cercare nelle prossime descrizioni (lascia vuoto per usare quello del movimento).',
      label: 'Testo da cercare',
      initialValue: '',
      confirmLabel: 'Crea regola',
    })
    if (pattern === null) return
    try {
      const rule = await bankApi.ruleFromMovement(m.id, pattern.trim() || undefined)
      setInfo(`Regola «${rule.name}» creata: «${rule.pattern}».`)
    } catch (e) {
      setError(errMsg(e, 'Impossibile creare la regola'))
    }
  }

  async function voidMovement(m: BankMovement) {
    const reason = await prompt({
      title: 'Annulla il movimento',
      label: 'Motivo dell\'annullamento',
      required: true,
      destructive: true,
      confirmLabel: 'Annulla movimento',
    })
    if (!reason?.trim()) return
    setError('')
    try {
      await bankApi.voidMovement(m.id, reason.trim())
      await load()
    } catch (e) {
      setError(errMsg(e, 'Impossibile annullare il movimento'))
    }
  }

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1

  return (
    <>
      <FilterBar
        actions={
          <>
            <SortResetLink sort={sort} defaultSort={MOVEMENTS_DEFAULT_SORT} onReset={() => handleSort(MOVEMENTS_DEFAULT_SORT)} />
            {canWrite && (
              <Button type="button" variant="filled" className="ms-auto" onClick={() => setShowForm(s => !s)}>
                {showForm ? 'Chiudi' : 'Nuovo movimento'}
              </Button>
            )}
          </>
        }
      >
        <div>
          <Select label="Conto" value={accountId} onChange={e => { setAccountId(e.target.value); setPage(1) }}>
            <option value="">Tutti</option>
            {lookups.accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
          </Select>
        </div>
        <div>
          <TextField label="Dal" type="date" value={from} onChange={e => { setFrom(e.target.value); setPage(1) }} />
        </div>
        <div>
          <TextField label="Al" type="date" value={to} onChange={e => { setTo(e.target.value); setPage(1) }} />
        </div>
        <div>
          <Select label="Stato" value={status} onChange={e => { setStatus(e.target.value); setPage(1) }}>
            <option value="">Tutti</option>
            {(Object.keys(STATUS_LABELS) as ReconciliationStatus[]).map(s => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
          </Select>
        </div>
        <div>
          <Select label="Verso" value={direction} onChange={e => { setDirection(e.target.value); setPage(1) }}>
            <option value="">Tutti</option>
            <option value="Entrata">Entrate</option>
            <option value="Uscita">Uscite</option>
          </Select>
        </div>
        <Checkbox id="bank-voided" label="Mostra annullati" checked={includeVoided} onChange={e => setIncludeVoided(e.target.checked)} />
      </FilterBar>

      {showForm && canWrite && (
        <NewMovementForm lookups={lookups} onSaved={() => { setShowForm(false); void load() }} />
      )}

      {error && <Callout tone="error">{error}</Callout>}
      {info && <Callout tone="success">{info}</Callout>}

      {data && (
        <div className="d-flex gap-4 mb-2 md-typescale-body-small flex-wrap">
          <span><strong>Entrate:</strong> {eur(data.summary.incomes)} ({data.summary.incomesCount})</span>
          <span><strong>Uscite:</strong> {eur(data.summary.outflows)} ({data.summary.outflowsCount})</span>
          <span className="text-muted">{data.summary.note}</span>
        </div>
      )}

      {dimTarget && (
        <MovementDimensionsDialog
          movement={dimTarget}
          dims={dims}
          onClose={() => setDimTarget(null)}
          onSaved={async () => { setDimTarget(null); setInfo('Dimensioni del movimento salvate.'); await load() }}
        />
      )}

      <Card padding="none">
        <DataTable
          variant="plain"
          footer={data && totalPages > 1 ? (
            <Pagination page={page} pageSize={data.pageSize} total={data.total} onChange={setPage} />
          ) : undefined}
        >
            <thead>
              <tr>
                <SortKeyTh label="Data" ascKey="date_asc" descKey="date_desc" sort={sort} onSort={handleSort} className="ps-3" />
                <th>Conto</th><th>Descrizione</th>
                <th className="text-end">Importo</th><th>Categoria</th><th>Stato</th>
                {canWrite && <th className="text-end pe-3">Azioni</th>}
              </tr>
            </thead>
            <tbody>
              {loading && !data && <tr><td colSpan={7} className="text-center py-4"><CircularProgress size="sm" /></td></tr>}
              {data?.items.length === 0 && <DataTableEmptyRow colSpan={7} title="Nessun movimento." />}
              {data?.items.map(m => (
                <tr key={m.id} className={m.voided ? 'text-decoration-line-through text-muted' : ''}>
                  <td className="ps-3 text-nowrap">{fmtDate(m.operationDate)}</td>
                  <td>{m.accountName ?? m.accountId}</td>
                  <td>
                    <div>{m.bankDescription}</div>
                    {m.counterparty && <div className="md-typescale-body-small text-muted">{m.counterparty}</div>}
                    {m.counterpartyIban && <div className="md-typescale-body-small text-muted">{m.counterpartyIban}</div>}
                    {m.kind === 'TrasferimentoInterno' && <StatusPill tone="info">Trasferimento interno</StatusPill>}
                    {m.voided && m.voidReason && <div className="md-typescale-body-small">Annullato: {m.voidReason}</div>}
                  </td>
                  <td className={`text-end text-nowrap ${m.direction === 'Entrata' ? 'text-success' : ''}`}>
                    {m.direction === 'Entrata' ? '+' : '−'}{eur(m.amount)}
                  </td>
                  <td className="md-u-minw-168">
                    {m.kind === 'TrasferimentoInterno' || m.voided || !canWrite ? (
                      <span className="md-typescale-body-small">
                        {m.direction === 'Uscita'
                          ? lookups.categories.find(c => c.id === m.expenseCategoryId)?.name ?? '—'
                          : m.incomeCategory ?? '—'}
                      </span>
                    ) : m.direction === 'Uscita' ? (
                      <Select labelHidden label="Categoria di spesa" value={m.expenseCategoryId ?? ''} onChange={e => void classify(m, e.target.value)}>
                        <option value="">—</option>
                        {lookups.categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </Select>
                    ) : (
                      <Select labelHidden label="Categoria di incasso" value={m.incomeCategory ?? ''} onChange={e => void classify(m, e.target.value)}>
                        <option value="">—</option>
                        {INCOME_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                      </Select>
                    )}
                  </td>
                  <td>
                    <StatusPill tone={statusBadge(m.reconciliationStatus)}>{STATUS_LABELS[m.reconciliationStatus]}</StatusPill>
                    {m.reconciliationStatus === 'Parziale' && <div className="md-typescale-body-small text-muted">resta {eur(m.remainingAmount)}</div>}
                  </td>
                  {canWrite && (
                    <td className="text-end pe-3 text-nowrap">
                      {dims.enabled && !m.voided && m.kind === 'Ordinario' && (
                        <IconButton icon="account_tree" size="sm" aria-label="Imposta BU, sede, team e categoria di ricavo" onClick={() => setDimTarget(m)} />
                      )}
                      {!m.voided && m.kind === 'Ordinario' && (m.expenseCategoryId || m.incomeCategory || m.supplierId) && (
                        <IconButton icon="rule" size="sm" aria-label="Salva come regola" onClick={() => void saveRule(m)} />
                      )}
                      {!m.voided && m.kind === 'Ordinario' && m.reconciliationStatus === 'DaRiconciliare' && (
                        <IconButton icon="cancel" size="sm" aria-label="Annulla movimento" onClick={() => void voidMovement(m)} />
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </DataTable>
      </Card>
    </>
  )
}

function NewMovementForm({ lookups, onSaved }: { lookups: Lookups; onSaved: () => void }) {
  const [accountId, setAccountId] = useState('')
  const [date, setDate] = useState('')
  const [direction, setDirection] = useState<Direction>('Uscita')
  const [amount, setAmount] = useState('')
  const [description, setDescription] = useState('')
  const [counterparty, setCounterparty] = useState('')
  const [notes, setNotes] = useState('')
  const [error, setError] = useState('')
  const [duplicate, setDuplicate] = useState(false)
  const [busy, setBusy] = useState(false)

  async function save(force: boolean) {
    setBusy(true)
    setError('')
    try {
      await bankApi.createMovement({
        accountId: Number(accountId),
        operationDate: date,
        amount: Number(amount.replace(',', '.')),
        direction,
        bankDescription: description,
        counterparty: counterparty || undefined,
        notes: notes || undefined,
        force,
      })
      onSaved()
    } catch (e) {
      const msg = errMsg(e, 'Impossibile salvare il movimento')
      setDuplicate(msg.includes('identico'))
      setError(msg)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card className="mb-3">
      <CardContent>
        <FormGrid columns={4}>
          <div>
            <Select label="Conto" value={accountId} onChange={e => setAccountId(e.target.value)}>
              <option value="">Scegli…</option>
              {lookups.accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
            </Select>
          </div>
          <div>
            <TextField label="Data" type="date" value={date} onChange={e => setDate(e.target.value)} />
          </div>
          <div>
            <Select label="Verso" value={direction} onChange={e => setDirection(e.target.value as Direction)}>
              <option value="Uscita">Uscita</option>
              <option value="Entrata">Entrata</option>
            </Select>
          </div>
          <div>
            <TextField label="Importo (€)" inputMode="decimal" value={amount} onChange={e => setAmount(e.target.value)} />
          </div>
          <div>
            <TextField label="Controparte" maxLength={200} value={counterparty} onChange={e => setCounterparty(e.target.value)} />
          </div>
          <div className="md-span-2">
            <TextField label="Descrizione" maxLength={500} value={description} onChange={e => setDescription(e.target.value)} />
          </div>
          <div className="md-span-2">
            <TextField label="Note" maxLength={1000} value={notes} onChange={e => setNotes(e.target.value)} />
          </div>
        </FormGrid>
        {error && <Callout tone="error">{error}</Callout>}
        <div className="mt-3 d-flex gap-2">
          <Button type="button" variant="tonal" disabled={busy || !accountId || !date || !amount || !description.trim()} onClick={() => void save(false)}>
            Salva
          </Button>
          {duplicate && (
            <Button type="button" variant="text" disabled={busy} onClick={() => void save(true)}>Registra comunque</Button>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

/* ── Importa estratto conto ──────────────────────────────────────────────────────────────────── */

const EMPTY_PROFILE: CsvProfile = { dateColumn: 'Data', descriptionColumn: 'Descrizione', amountColumn: 'Importo' }

function ImportTab({ lookups, canWrite }: { lookups: Lookups; canWrite: boolean }) {
  const [accountId, setAccountId] = useState('')
  const prompt = usePrompt()
  const [mode, setMode] = useState<'signed' | 'split'>('signed')
  const [profile, setProfile] = useState<CsvProfile>(EMPTY_PROFILE)
  const [csv, setCsv] = useState('')
  const [fileName, setFileName] = useState('')
  const [preview, setPreview] = useState<ImportPreview | null>(null)
  const [batches, setBatches] = useState<ImportBatch[]>([])
  const [error, setError] = useState('')
  const [info, setInfo] = useState('')
  const [busy, setBusy] = useState(false)

  const loadBatches = useCallback(async () => {
    try {
      setBatches((await bankApi.imports(accountId ? Number(accountId) : undefined)).items)
    } catch (e) {
      setError(errMsg(e, 'Errore durante il caricamento dei lotti'))
    }
  }, [accountId])

  useEffect(() => { void loadBatches() }, [loadBatches])

  useEffect(() => {
    if (!accountId) return
    void (async () => {
      try {
        const stored = (await bankApi.profile(Number(accountId))).profile
        if (stored) {
          setProfile(stored)
          setMode(stored.debitColumn || stored.creditColumn ? 'split' : 'signed')
        } else {
          setProfile(EMPTY_PROFILE)
          setMode('signed')
        }
      } catch {
        setProfile(EMPTY_PROFILE)
      }
    })()
  }, [accountId])

  function field(key: keyof CsvProfile, label: string, placeholder = '') {
    return (
      <div>
        <TextField label={label}
          placeholder={placeholder}
          value={(profile[key] as string | null | undefined) ?? ''}
          onChange={e => setProfile(p => ({ ...p, [key]: e.target.value }))}
        />
      </div>
    )
  }

  function request(save: boolean) {
    const p: CsvProfile = mode === 'signed'
      ? { ...profile, debitColumn: null, creditColumn: null }
      : { ...profile, amountColumn: null }
    return { accountId: Number(accountId), csv, fileName: fileName || undefined, profile: p, saveProfile: save }
  }

  async function onFile(file: File | undefined) {
    if (!file) return
    setFileName(file.name)
    setCsv(await file.text())
    setPreview(null)
    setInfo('')
  }

  async function runPreview() {
    setBusy(true)
    setError('')
    setInfo('')
    try {
      setPreview(await bankApi.previewImport(request(false)))
    } catch (e) {
      setPreview(null)
      setError(errMsg(e, 'Impossibile leggere il file'))
    } finally {
      setBusy(false)
    }
  }

  async function runApply() {
    setBusy(true)
    setError('')
    try {
      const res = await bankApi.applyImport(request(true))
      setInfo(res.newRows === 0
        ? `Nessun movimento nuovo: ${res.duplicateRows} già presenti${res.errorRows ? `, ${res.errorRows} righe con errore` : ''}.`
        : `Importati ${res.newRows} movimenti nuovi (${res.duplicateRows} già presenti, ${res.errorRows} con errore, ${res.rulesApplied ?? 0} categorizzati dalle regole).`)
      setPreview(null)
      await loadBatches()
    } catch (e) {
      setError(errMsg(e, 'Importazione non riuscita'))
    } finally {
      setBusy(false)
    }
  }

  async function undo(b: ImportBatch) {
    const reason = await prompt({
      title: 'Annulla il lotto',
      label: 'Motivo dell\'annullamento del lotto',
      required: true,
      destructive: true,
      confirmLabel: 'Annulla lotto',
    })
    if (!reason?.trim()) return
    setError('')
    try {
      const res = await bankApi.undoImport(b.id, reason.trim())
      setInfo(`Lotto annullato: ${res.removedMovements} movimenti rimossi.`)
      await loadBatches()
    } catch (e) {
      setError(errMsg(e, 'Impossibile annullare il lotto'))
    }
  }

  return (
    <>
      {canWrite && (
        <Card className="mb-3">
          <CardContent>
            <Callout tone="info">
              Carica il CSV scaricato dalla banca (separatore «;» o «,», date gg/mm/aaaa, decimali con la virgola, massimo
              5 MB e 10.000 righe). Excel e collegamenti bancari non sono supportati. Lo stesso file importato due volte
              non crea doppioni. Mai IBAN: non vengono letti né salvati.
            </Callout>
            <FormGrid columns={3} className="mb-3">
              <div>
                <Select label="Conto" value={accountId} onChange={e => { setAccountId(e.target.value); setPreview(null) }}>
                  <option value="">Scegli…</option>
                  {lookups.accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                </Select>
              </div>
              <div>
                <TextField label="File CSV" type="file" accept=".csv,text/csv" onChange={e => void onFile(e.target.files?.[0])} />
              </div>
              <div>
                <Select label="Importi" value={mode} onChange={e => setMode(e.target.value as 'signed' | 'split')}>
                  <option value="signed">Una colonna con segno (negativo = uscita)</option>
                  <option value="split">Colonne Dare / Avere</option>
                </Select>
              </div>
            </FormGrid>
            <FormGrid columns={4} className="mb-3">
              {field('dateColumn', 'Colonna data', 'Data')}
              {field('descriptionColumn', 'Colonna descrizione', 'Descrizione')}
              {mode === 'signed' ? field('amountColumn', 'Colonna importo', 'Importo') : (
                <>
                  {field('debitColumn', 'Colonna Dare', 'Dare')}
                  {field('creditColumn', 'Colonna Avere', 'Avere')}
                </>
              )}
              {field('counterpartyColumn', 'Controparte (opz.)')}
              {field('referenceColumn', 'Causale (opz.)')}
              {field('counterpartyIbanColumn', 'IBAN controparte (opz.)')}
              {field('valueDateColumn', 'Data valuta (opz.)')}
            </FormGrid>
            <div className="d-flex gap-2">
              <Button type="button" variant="text" disabled={busy || !accountId || !csv} onClick={() => void runPreview()}>
                {busy ? 'Attendi…' : 'Anteprima'}
              </Button>
              <Button type="button" variant="tonal" disabled={busy || !accountId || !csv || !preview || preview.newRows === 0} onClick={() => void runApply()}>
                Importa {preview ? `${preview.newRows} movimenti nuovi` : ''}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {error && <Callout tone="error">{error}</Callout>}
      {info && <Callout tone="success">{info}</Callout>}

      {preview && (
        <Card className="mb-3">
          <CardContent>
            <div className="d-flex gap-3 flex-wrap mb-2 md-typescale-body-small">
              <span><strong>Righe:</strong> {preview.totalRows}</span>
              <span className="text-success"><strong>Nuove:</strong> {preview.newRows}</span>
              <span className="text-muted"><strong>Già presenti:</strong> {preview.duplicateRows}</span>
              <span className={preview.errorRows ? 'text-danger' : 'text-muted'}><strong>Con errore:</strong> {preview.errorRows}</span>
              <span><strong>Categorie proposte:</strong> {preview.proposedRows}</span>
            </div>
            {preview.truncated && <div className="md-typescale-body-small text-muted mb-2">Mostrate le prime {preview.rows.length} righe.</div>}
            <DataTable variant="plain" stickyHeader maxHeight="420px">
                <thead>
                  <tr><th>#</th><th>Data</th><th>Descrizione</th><th className="text-end">Importo</th><th>Esito</th><th>Categoria proposta</th></tr>
                </thead>
                <tbody>
                  {preview.rows.map(r => (
                    <tr key={r.rowNumber} className={r.error ? 'table-danger' : r.isDuplicate ? 'text-muted' : ''}>
                      <td>{r.rowNumber}</td>
                      <td className="text-nowrap">{fmtDate(r.operationDate)}</td>
                      <td>{r.description}</td>
                      <td className="md-num text-end text-nowrap">{r.error ? '—' : `${r.direction === 'Entrata' ? '+' : '−'}${eur(r.amount)}`}</td>
                      <td>{r.error ?? (r.isDuplicate ? 'Già presente' : 'Nuovo')}</td>
                      <td className="md-typescale-body-small">
                        {r.proposed
                          ? `${r.proposed.ruleName ?? 'Regola'} → ${lookups.categories.find(c => c.id === r.proposed?.expenseCategoryId)?.name ?? r.proposed.incomeCategory ?? 'controparte'}`
                          : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </DataTable>
          </CardContent>
        </Card>
      )}

      <h3 className="md-typescale-title-medium">Lotti importati</h3>
      <Card padding="none">
        <DataTable variant="plain">
            <thead>
              <tr><th className="ps-3">Data</th><th>File</th><th>Conto</th><th className="text-end">Nuovi</th><th className="text-end">Doppioni</th><th className="text-end">Errori</th><th>Stato</th>{canWrite && <th />}</tr>
            </thead>
            <tbody>
              {batches.length === 0 && <DataTableEmptyRow colSpan={8} title="Nessun lotto." />}
              {batches.map(b => (
                <tr key={b.id}>
                  <td className="ps-3 text-nowrap">{fmtDate(b.createdAt)}</td>
                  <td>{b.fileName}</td>
                  <td>{lookups.accounts.find(a => a.id === b.accountId)?.name ?? b.accountId}</td>
                  <td className="text-end">{b.newRows}</td>
                  <td className="text-end">{b.duplicateRows}</td>
                  <td className="text-end">{b.errorRows}</td>
                  <td>
                    <StatusPill tone={b.status === 'Applicato' ? 'success' : 'neutral'}>{b.status}</StatusPill>
                    {b.undoReason && <div className="md-typescale-body-small text-muted">{b.undoReason}</div>}
                  </td>
                  {canWrite && (
                    <td className="text-end pe-3">
                      {b.status === 'Applicato' && (
                        <IconButton icon="undo" size="sm" aria-label="Annulla lotto" onClick={() => void undo(b)} />
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </DataTable>
      </Card>
    </>
  )
}

/* ── Regole ──────────────────────────────────────────────────────────────────────────────────── */

function RulesTab({ lookups, canWrite }: { lookups: Lookups; canWrite: boolean }) {
  const confirm = useConfirm()
  const [rules, setRules] = useState<MovementRule[]>([])
  const [error, setError] = useState('')
  const [name, setName] = useState('')
  const [matchField, setMatchField] = useState('Descrizione')
  const [operator, setOperator] = useState('contains')
  const [pattern, setPattern] = useState('')
  const [direction, setDirection] = useState('')
  const [categoryId, setCategoryId] = useState('')
  const [incomeCategory, setIncomeCategory] = useState('')
  const [supplierId, setSupplierId] = useState('')
  const [priority, setPriority] = useState('100')

  const load = useCallback(async () => {
    try {
      setRules((await bankApi.rules()).items)
    } catch (e) {
      setError(errMsg(e, 'Errore durante il caricamento delle regole'))
    }
  }, [])

  useEffect(() => { void load() }, [load])

  async function create() {
    setError('')
    try {
      await bankApi.createRule({
        name,
        matchField,
        operator,
        pattern,
        direction: direction || null,
        expenseCategoryId: categoryId ? Number(categoryId) : null,
        incomeCategory: incomeCategory || null,
        supplierId: supplierId ? Number(supplierId) : null,
        priority: Number(priority) || 100,
      })
      setName(''); setPattern(''); setCategoryId(''); setIncomeCategory(''); setSupplierId('')
      await load()
    } catch (e) {
      setError(errMsg(e, 'Impossibile creare la regola'))
    }
  }

  async function toggle(r: MovementRule) {
    setError('')
    try {
      await bankApi.updateRule(r.id, {
        name: r.name, matchField: r.matchField, operator: r.operator, pattern: r.pattern, direction: r.direction,
        accountId: r.accountId, expenseCategoryId: r.expenseCategoryId, incomeCategory: r.incomeCategory,
        supplierId: r.supplierId, priority: r.priority, isActive: !r.isActive,
      })
      await load()
    } catch (e) {
      setError(errMsg(e, 'Impossibile aggiornare la regola'))
    }
  }

  async function remove(r: MovementRule) {
    if (!(await confirm({ title: 'Elimina regola', message: `Eliminare la regola «${r.name}»?`, confirmLabel: 'Elimina', destructive: true }))) return
    setError('')
    try {
      await bankApi.deleteRule(r.id)
      await load()
    } catch (e) {
      setError(errMsg(e, 'Impossibile eliminare la regola'))
    }
  }

  return (
    <>
      <p className="text-muted md-typescale-body-small">
        Le regole PROPONGONO categoria e controparte quando un movimento entra (da CSV o a mano): vince la prima per
        priorità (numero più basso). Si cerca solo nel testo di descrizione, causale o controparte — niente IBAN e niente
        espressioni regolari.
      </p>

      {canWrite && (
        <Card className="mb-3">
          <CardContent>
            <FormGrid columns={4}>
              <div>
                <TextField label="Nome" maxLength={100} value={name} onChange={e => setName(e.target.value)} />
              </div>
              <div>
                <Select label="Campo" value={matchField} onChange={e => setMatchField(e.target.value)}>
                  <option>Descrizione</option><option>Causale</option><option>Controparte</option><option>Iban</option>
                </Select>
              </div>
              <div>
                <Select label="Operatore" value={operator} onChange={e => setOperator(e.target.value)}>
                  <option value="contains">contiene</option>
                  <option value="startsWith">inizia con</option>
                  <option value="equals">è uguale a</option>
                </Select>
              </div>
              <div>
                <TextField label="Testo (max 100)" maxLength={100} value={pattern} onChange={e => setPattern(e.target.value)} />
              </div>
              <div>
                <Select label="Verso" value={direction} onChange={e => setDirection(e.target.value)}>
                  <option value="">Entrambi</option><option value="Uscita">Uscita</option><option value="Entrata">Entrata</option>
                </Select>
              </div>
              <div>
                <Select label="Categoria di spesa" value={categoryId} onChange={e => setCategoryId(e.target.value)}>
                  <option value="">—</option>
                  {lookups.categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </Select>
              </div>
              <div>
                <Select label="Categoria di incasso" value={incomeCategory} onChange={e => setIncomeCategory(e.target.value)}>
                  <option value="">—</option>
                  {INCOME_CATEGORIES.map(c => <option key={c}>{c}</option>)}
                </Select>
              </div>
              <div>
                <Select label="Fornitore" value={supplierId} onChange={e => setSupplierId(e.target.value)}>
                  <option value="">—</option>
                  {lookups.suppliers.map(s => <option key={s.id} value={s.id}>{s.companyName}</option>)}
                </Select>
              </div>
              <div>
                <TextField label="Priorità" inputMode="numeric" value={priority} onChange={e => setPriority(e.target.value)} />
              </div>
              <div className="d-flex align-items-end">
                <Button type="button" variant="tonal" className="w-100" disabled={!name.trim() || !pattern.trim()} onClick={() => void create()}>Aggiungi</Button>
              </div>
            </FormGrid>
          </CardContent>
        </Card>
      )}

      {error && <Callout tone="error">{error}</Callout>}

      <Card padding="none">
        <DataTable>
            <thead>
              <tr><th className="ps-3">Priorità</th><th>Nome</th><th>Condizione</th><th>Propone</th><th className="text-end">Usi</th><th>Attiva</th>{canWrite && <th />}</tr>
            </thead>
            <tbody>
              {rules.length === 0 && <DataTableEmptyRow colSpan={7} title="Nessuna regola." />}
              {rules.map(r => (
                <tr key={r.id} className={r.isActive ? '' : 'text-muted'}>
                  <td className="ps-3">{r.priority}</td>
                  <td>{r.name}</td>
                  <td className="md-typescale-body-small">{r.matchField} {r.operator === 'contains' ? 'contiene' : r.operator === 'startsWith' ? 'inizia con' : 'è uguale a'} «{r.pattern}»{r.direction ? ` (${r.direction})` : ''}</td>
                  <td className="md-typescale-body-small">
                    {[
                      lookups.categories.find(c => c.id === r.expenseCategoryId)?.name,
                      r.incomeCategory,
                      lookups.suppliers.find(s => s.id === r.supplierId)?.companyName,
                    ].filter(Boolean).join(' · ') || '—'}
                  </td>
                  <td className="text-end">{r.timesApplied}</td>
                  <td>{r.isActive ? 'Sì' : 'No'}</td>
                  {canWrite && (
                    <td className="text-end pe-3 text-nowrap">
                      <Button type="button" variant="text" size="sm" onClick={() => void toggle(r)}>{r.isActive ? 'Disattiva' : 'Attiva'}</Button>
                      <IconButton icon="delete" size="sm" aria-label="Elimina" onClick={() => void remove(r)} />
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </DataTable>
      </Card>
    </>
  )
}

/* ── Riconciliazione ─────────────────────────────────────────────────────────────────────────── */

interface SplitRow {
  targetType: AllocationTargetType
  targetId: string
  amount: string
  note: string
}

function ReconcileTab({ canWrite, isAdmin }: { canWrite: boolean; isAdmin: boolean }) {
  const dimsOn = useFinanceDimensions().enabled
  const [list, setList] = useState<BankMovement[]>([])
  const prompt = usePrompt()
  const [selected, setSelected] = useState<BankMovement | null>(null)
  const [allocations, setAllocations] = useState<MovementAllocation[]>([])
  const [suggestions, setSuggestions] = useState<SuggestionsResponse | null>(null)
  const [candidates, setCandidates] = useState<TransferCandidate[]>([])
  const [rows, setRows] = useState<SplitRow[]>([{ targetType: 'Altro', targetId: '', amount: '', note: '' }])
  const [error, setError] = useState('')
  const [info, setInfo] = useState('')

  const loadList = useCallback(async () => {
    try {
      const pending = await bankApi.movements({ status: 'DaRiconciliare', kind: 'Ordinario', pageSize: 100 })
      const partial = await bankApi.movements({ status: 'Parziale', kind: 'Ordinario', pageSize: 100 })
      setList([...partial.items, ...pending.items])
      setCandidates((await bankApi.transferCandidates()).items)
    } catch (e) {
      setError(errMsg(e, 'Errore durante il caricamento dei movimenti'))
    }
  }, [])

  useEffect(() => { void loadList() }, [loadList])

  const open = useCallback(async (id: number) => {
    setError('')
    try {
      const [detail, sugg] = await Promise.all([bankApi.movement(id), bankApi.suggestions(id)])
      setSelected(detail.movement)
      setAllocations(detail.allocations)
      setSuggestions(sugg)
      setRows([{ targetType: 'Altro', targetId: '', amount: '', note: '' }])
    } catch (e) {
      setError(errMsg(e, 'Errore durante il caricamento del movimento'))
    }
  }, [])

  async function allocate(items: AllocationItem[]) {
    if (!selected) return
    setError('')
    setInfo('')
    let closedReason: string | undefined
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        const res = await bankApi.allocate(selected.id, items, closedReason)
        setInfo(res.statusChanges.length > 0 ? `Abbinamento salvato. Stati aggiornati: ${res.statusChanges.map(s => s.status).join(', ')}.` : 'Abbinamento salvato.')
        await open(selected.id)
        await loadList()
        return
      } catch (e) {
        const msg = errMsg(e, 'Abbinamento non riuscito')
        if (attempt === 0 && isAdmin && msg.includes('chiuso')) {
          const reason = await prompt({
            title: 'Abbina in un periodo chiuso',
            message: `${msg} Per procedere indica il motivo (resta in audit).`,
            label: 'Motivo',
            required: true,
            confirmLabel: 'Procedi',
          })
          if (reason?.trim()) { closedReason = reason.trim(); continue }
        }
        setError(msg)
        return
      }
    }
  }

  async function removeAllocation(a: MovementAllocation) {
    const reason = await prompt({
      title: 'Rimuovi la quota',
      label: 'Motivo della rimozione',
      required: true,
      destructive: true,
      confirmLabel: 'Rimuovi',
    })
    if (!reason?.trim() || !selected) return
    setError('')
    try {
      const res = await bankApi.deallocate(a.id, reason.trim())
      setInfo(res.note ?? 'Quota rimossa.')
      await open(selected.id)
      await loadList()
    } catch (e) {
      setError(errMsg(e, 'Impossibile rimuovere la quota'))
    }
  }

  async function confirmTransfer(c: TransferCandidate) {
    setError('')
    try {
      await bankApi.linkTransfer(c.outflow.id, c.inflow.id)
      setInfo('Giroconto registrato: i due movimenti non contano come costo né come incasso.')
      await loadList()
    } catch (e) {
      setError(errMsg(e, 'Impossibile registrare il giroconto'))
    }
  }

  const splitItems = useMemo<AllocationItem[]>(() => rows
    .filter(r => r.amount.trim() !== '')
    .map(r => ({
      targetType: r.targetType,
      amount: Number(r.amount.replace(',', '.')),
      ...(r.targetType !== 'Altro' && r.targetId ? { targetId: Number(r.targetId) } : {}),
      ...(r.note.trim() ? { note: r.note.trim() } : {}),
    })), [rows])

  const splitTotal = splitItems.reduce((s, i) => s + (Number.isFinite(i.amount) ? i.amount : 0), 0)

  return (
    <PaneLayout
      variant="list-detail"
      paneLabel="Movimenti da riconciliare"
      mainLabel="Dettaglio del movimento"
      pane={
        <>
        {error && <Callout tone="error">{error}</Callout>}
        {info && <Callout tone="success">{info}</Callout>}

        {candidates.length > 0 && (
          <Card padding="none">
            <CardHeader title={<>Possibili giroconti ({candidates.length})</>} headingLevel={3} />
            <List aria-label="Possibili giroconti">
              {candidates.slice(0, 10).map(c => (
                <ListItem
                  key={`${c.outflow.id}-${c.inflow.id}`}
                  headline={`${eur(c.outflow.amount)}: ${c.outflow.accountName} (${fmtDate(c.outflow.operationDate)}) → ${c.inflow.accountName} (${fmtDate(c.inflow.operationDate)})`}
                  trailing={canWrite ? <Button type="button" variant="text" size="sm" onClick={() => void confirmTransfer(c)}>Conferma giroconto</Button> : undefined}
                />
              ))}
            </List>
          </Card>
        )}

        <Card padding="none">
          <CardHeader title={<>Da riconciliare ({list.length})</>} headingLevel={3} />
          <div className="md-u-scroll-y-520">
            {list.length === 0 && <EmptyState icon="task_alt" title="Tutto riconciliato." />}
            <List aria-label="Movimenti da riconciliare">
              {list.map(m => (
                <ListItem
                  key={m.id}
                  selected={selected?.id === m.id}
                  onClick={() => void open(m.id)}
                  headline={`${fmtDate(m.operationDate)} · ${m.accountName}`}
                  supporting={<>{m.bankDescription}{m.reconciliationStatus === 'Parziale' && <> · Parziale: resta {eur(m.remainingAmount)}</>}</>}
                  trailing={<strong>{m.direction === 'Entrata' ? '+' : '−'}{eur(m.amount)}</strong>}
                />
              ))}
            </List>
          </div>
        </Card>
        </>
      }
      main={
        <>
        {!selected && <div className="text-muted">Scegli un movimento a sinistra per vedere le proposte e abbinarlo.</div>}
        {selected && suggestions && (
          <>
            <Card>
              <CardContent>
                <div className="d-flex justify-content-between flex-wrap gap-2">
                  <div>
                    <div className="md-typescale-emphasized-body-medium">{selected.bankDescription}</div>
                    <div className="md-typescale-body-small text-muted">{fmtDate(selected.operationDate)} · {selected.accountName}{selected.counterparty ? ` · ${selected.counterparty}` : ''}</div>
                  </div>
                  <div className="text-end">
                    <div className="md-typescale-title-large">{selected.direction === 'Entrata' ? '+' : '−'}{eur(selected.amount)}</div>
                    <StatusPill tone={statusBadge(selected.reconciliationStatus)}>{STATUS_LABELS[selected.reconciliationStatus]}</StatusPill>
                    <div className="md-typescale-body-small text-muted">Da abbinare: {eur(suggestions.remaining)}</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader
                headingLevel={3}
                title={<>Proposte — <span className={suggestions.level === 'MatchCerto' ? 'text-success' : suggestions.level === 'MatchProbabile' ? 'text-warning' : 'text-muted'}>{MATCH_LABELS[suggestions.level]}</span></>}
              />
              <List aria-label="Proposte di abbinamento">
                {suggestions.suggestions.length === 0 && <ListItem headline="Nessuna proposta: usa l'abbinamento manuale qui sotto." />}
                {suggestions.suggestions.map(s => (
                  <ListItem
                    key={`${s.targetType}-${s.targetId}`}
                    headline={<><StatusPill tone={s.level === 'MatchCerto' ? 'success' : 'warning'} className="me-2">{MATCH_LABELS[s.level]}</StatusPill>{s.label}</>}
                    supporting={`${s.reason}${s.score ? ` Punteggio ${s.score}.` : ""} Residuo ${eur(s.residual)}${s.dueDate ? ` · scade ${fmtDate(s.dueDate)}` : ''}`}
                    trailing={canWrite ? (
                      <Button
                        type="button" variant="text" size="sm" className="text-nowrap"
                        onClick={() => void allocate([{ targetType: s.targetType, targetId: s.targetId, amount: Math.min(s.residual, suggestions.remaining) }])}
                      >
                        Abbina {eur(Math.min(s.residual, suggestions.remaining))}
                      </Button>
                    ) : undefined}
                  />
                ))}
              </List>
            </Card>

            {allocations.length > 0 && (
              <Card padding="none">
                <CardHeader title="Quote del movimento" headingLevel={3} />
                <List aria-label="Quote del movimento">
                  {allocations.map(a => (
                    <ListItem
                      key={a.id}
                      className={a.active ? '' : 'text-muted text-decoration-line-through'}
                      headline={<>{a.targetType}{a.targetId ? ` #${a.targetId}` : ''} — {eur(a.amount)}{a.note && <span className="md-typescale-body-small text-muted"> · {a.note}</span>}</>}
                      supporting={!a.active && a.removedReason ? `Rimossa: ${a.removedReason}` : undefined}
                      trailing={canWrite && a.active ? <IconButton icon="delete" size="sm" aria-label="Rimuovi" onClick={() => void removeAllocation(a)} /> : undefined}
                    />
                  ))}
                </List>
              </Card>
            )}

            {canWrite && suggestions.remaining > 0 && (
              <Card>
                <CardHeader title="Abbinamento manuale e split" headingLevel={3} />
                <CardContent>
                  {rows.map((r, i) => (
                    <FormGrid columns={3} key={i} className="mb-2">
                      <div>
                        <Select labelHidden label="Tipo di abbinamento"
                          value={r.targetType}
                          onChange={e => setRows(rs => rs.map((x, j) => j === i ? { ...x, targetType: e.target.value as AllocationTargetType } : x))}
                        >
                          <option value="Spesa">Spesa</option>
                          <option value="Fattura">Fattura</option>
                          <option value="MovimentoSocio">Mov. socio</option>
                          {dimsOn && <option value="DistintaProvvigioni">Distinta provvigioni</option>}
                          <option value="Altro">Altro</option>
                        </Select>
                      </div>
                      <div>
                        <TextField
                          labelHidden label="N. (id)" placeholder="N. (id)"
                          disabled={r.targetType === 'Altro'}
                          value={r.targetId}
                          onChange={e => setRows(rs => rs.map((x, j) => j === i ? { ...x, targetId: e.target.value } : x))}
                        />
                      </div>
                      <div>
                        <TextField
                          labelHidden label="Importo" placeholder="Importo"
                          inputMode="decimal"
                          value={r.amount}
                          onChange={e => setRows(rs => rs.map((x, j) => j === i ? { ...x, amount: e.target.value } : x))}
                        />
                      </div>
                      <div className="md-span-2">
                        <TextField
                          labelHidden
                          label="Nota"
                          placeholder={r.targetType === 'Altro' ? 'Nota (obbligatoria)' : 'Nota'}
                          maxLength={500}
                          value={r.note}
                          onChange={e => setRows(rs => rs.map((x, j) => j === i ? { ...x, note: e.target.value } : x))}
                        />
                      </div>
                      <div>
                        <IconButton icon="close" size="sm" aria-label="Rimuovi quota" disabled={rows.length === 1} onClick={() => setRows(rs => rs.filter((_, j) => j !== i))} />
                      </div>
                    </FormGrid>
                  ))}
                  <div className="d-flex gap-2 align-items-center flex-wrap">
                    <Button type="button" variant="text" onClick={() => setRows(rs => [...rs, { targetType: 'Altro', targetId: '', amount: '', note: '' }])}>Aggiungi quota</Button>
                    <Button type="button" variant="tonal" disabled={splitItems.length === 0} onClick={() => void allocate(splitItems)}>Salva abbinamento</Button>
                    <span className={`md-typescale-body-small ${splitTotal > suggestions.remaining ? 'text-danger' : 'text-muted'}`}>
                      Somma {eur(splitTotal)} di {eur(suggestions.remaining)}
                    </span>
                  </div>
                  <div className="md-typescale-body-small text-muted mt-2">
                    Una spesa diventa «Pagata» (o «Parzialmente pagata») e una fattura «paid» quando le quote raggiungono il totale.
                    Gli stati finali non tornano indietro se rimuovi una quota.
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}
        </>
      }
    />
  )
}

/* ── Anomalie ────────────────────────────────────────────────────────────────────────────────── */

function AnomaliesTab() {
  const [days, setDays] = useState(7)
  const [data, setData] = useState<Anomalies | null>(null)
  const [status, setStatus] = useState<MonthStatus | null>(null)
  const [error, setError] = useState('')
  const now = new Date()

  useEffect(() => {
    void (async () => {
      try {
        setData(await bankApi.anomalies(days))
        setStatus(await bankApi.monthStatus(now.getFullYear(), now.getMonth() + 1))
      } catch (e) {
        setError(errMsg(e, 'Errore durante il caricamento delle anomalie'))
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days])

  return (
    <>
      <FilterBar>
        <TextField label="Oltre (giorni)" type="number" min={0} max={365} className="md-u-w-100" value={days} onChange={e => setDays(Math.max(0, Number(e.target.value) || 0))} />
      </FilterBar>

      {error && <Callout tone="error">{error}</Callout>}

      {status && (
        <Card padding="none" className="mb-3">
          <CardHeader
            headingLevel={3}
            title={<>Conti del mese {String(status.month).padStart(2, '0')}/{status.year} — {status.allReconciled ? 'tutti riconciliati' : 'da completare'}</>}
          />
          <List aria-label="Conti del mese">
            {status.accounts.map(a => (
              <ListItem
                key={a.accountId}
                headline={a.accountName}
                trailing={a.movements === 0 ? 'nessun movimento' : a.pending === 0 ? 'riconciliato' : `${a.pending} da riconciliare su ${a.movements}`}
              />
            ))}
          </List>
          <p className="md-typescale-body-small text-muted m-0 p-3">
            La voce «Conti bancari riconciliati» della chiusura mese resta una conferma manuale: qui è solo un aiuto.
          </p>
        </Card>
      )}

      {data && (
        <>
          <AnomalyTable title={`Non riconciliati da oltre ${data.days} giorni (${data.notReconciledCount})`} items={data.notReconciled} />
          <AnomalyTable title={`Riconciliati in parte da oltre ${data.days} giorni (${data.partialCount})`} items={data.partial} />
        </>
      )}
    </>
  )
}

function AnomalyTable({ title, items }: { title: string; items: Anomalies['notReconciled'] }) {
  return (
    <Card padding="none" className="mb-3">
      <CardHeader title={<>{title}</>} headingLevel={3} />
      <DataTable variant="plain">
          <thead>
            <tr><th className="ps-3">Data</th><th>Conto</th><th>Descrizione</th><th className="text-end">Importo</th><th className="text-end pe-3">Giorni</th></tr>
          </thead>
          <tbody>
            {items.length === 0 && <DataTableEmptyRow colSpan={5} title="Nessuna anomalia." />}
            {items.map(i => (
              <tr key={i.movement.id}>
                <td className="ps-3 text-nowrap">{fmtDate(i.movement.operationDate)}</td>
                <td>{i.movement.accountName}</td>
                <td>{i.movement.bankDescription}</td>
                <td className="md-num text-end text-nowrap">{i.movement.direction === 'Entrata' ? '+' : '−'}{eur(i.movement.amount)}</td>
                <td className="text-end pe-3">{i.ageDays}</td>
              </tr>
            ))}
          </tbody>
        </DataTable>
    </Card>
  )
}
